/**
 * Creative Save Service
 * 
 * Clean service for collecting creative data for database save.
 * Uses the single source of truth from creativeScheduleStorage.
 */

import { getAllSchedules, getScheduledDays, type CreativeSchedule } from '@/utils/creativeScheduleStorage';

export interface CreativeForSave {
  url: string;
  creativeId: string;
  locationName: string;
  orientation: 'portrait' | 'landscape';
  scheduledDays: number[];
}

/**
 * Generate a stable ID for a creative based on its URL
 */
export const generateCreativeId = (url: string, index: number): string => {
  if (!url) return `creative_${index}`;
  
  // Extract filename for readable ID
  try {
    const decoded = decodeURIComponent(url);
    const path = decoded.split("?")[0].split("#")[0];
    const filename = path.split("/").pop() || "";
    const cleanName = filename.replace(/\.[^.]+$/, "").substring(0, 20);
    return `${cleanName}_${index}`;
  } catch {
    return `creative_${index}`;
  }
};

/**
 * Determine orientation from URL patterns
 */
const determineOrientation = (url: string): 'portrait' | 'landscape' => {
  if (!url) return 'landscape';
  const lower = url.toLowerCase();
  if (lower.includes('portrait') || lower.includes('_p_') || lower.includes('-p-')) {
    return 'portrait';
  }
  // Check dimension patterns
  const match = lower.match(/(\d{3,4})x(\d{3,4})/);
  if (match) {
    const w = parseInt(match[1]);
    const h = parseInt(match[2]);
    return h > w ? 'portrait' : 'landscape';
  }
  return 'landscape';
};

/**
 * Extract location name from URL if embedded
 */
const extractLocationFromUrl = (url: string): string => {
  try {
    const decoded = decodeURIComponent(url);
    const filename = decoded.split("/").pop() || "";
    // Pattern: timestamp-LocationName-filename
    const parts = filename.split("-");
    if (parts.length >= 3) {
      // Skip timestamp, take middle parts as location
      const locationParts = parts.slice(1, -1);
      const location = locationParts.join(" ").replace(/_/g, " ").trim();
      if (location.length > 2 && location.length < 60) {
        return location;
      }
    }
  } catch {
    // Ignore
  }
  return "";
};

/**
 * Collect all creatives from localStorage sources and merge with schedule data
 */
export const collectCreativesForSave = (): CreativeForSave[] => {
  const creativeMap = new Map<string, CreativeForSave>();
  
  const safeJsonParse = <T>(key: string, fallback: T): T => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw || raw === "null" || raw === "undefined") return fallback;
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  };
  
  // Collect from all sources
  const sources: Array<{ data: any; locHint?: string }> = [];
  
  // General creatives
  const creatives = safeJsonParse<any[]>("creatives", []);
  creatives.forEach(c => {
    const url = typeof c === "string" ? c : c?.url;
    if (url) sources.push({ data: { url, ...c }, locHint: "" });
  });
  
  // Location-specific creatives
  const locationCreatives = safeJsonParse<Record<string, any[]>>("locationCreatives", {});
  Object.entries(locationCreatives).forEach(([loc, arr]) => {
    arr.forEach(c => {
      if (c?.url) sources.push({ data: c, locHint: loc });
    });
  });
  
  // Media owner creatives
  const mediaOwnerCreatives = safeJsonParse<Record<string, any[]>>("mediaOwnerCreatives", {});
  Object.entries(mediaOwnerCreatives).forEach(([loc, arr]) => {
    arr.forEach(c => {
      if (c?.url) sources.push({ data: c, locHint: loc });
    });
  });
  
  // All creative URLs
  const allUrls = safeJsonParse<string[]>("allCreativeUrls", []);
  allUrls.forEach(url => {
    if (url && !sources.some(s => s.data?.url === url)) {
      sources.push({ data: { url }, locHint: "" });
    }
  });
  
  // Process each source
  let index = 0;
  for (const { data, locHint } of sources) {
    const url = data?.url;
    if (!url || creativeMap.has(url)) continue;
    
    const locationName = data?.locationName || locHint || extractLocationFromUrl(url);
    
    // Get scheduled days from single source of truth
    const scheduledDays = getScheduledDays(url, locationName);
    
    // Also check if data has days (from React state)
    const dataDays = Array.isArray(data?.scheduledDays) ? data.scheduledDays : [];
    const finalDays = scheduledDays.length > 0 ? scheduledDays : dataDays;
    
    creativeMap.set(url, {
      url,
      creativeId: generateCreativeId(url, index),
      locationName,
      orientation: data?.orientation || determineOrientation(url),
      scheduledDays: finalDays.map(Number).filter(d => d > 0).sort((a, b) => a - b),
    });
    
    index++;
  }
  
  const result = Array.from(creativeMap.values());
  
  console.log("[CreativeSaveService] Collected creatives:");
  result.forEach((c, i) => {
    console.log(`  [${i}] ${c.creativeId}: days=[${c.scheduledDays.join(",")}] @ "${c.locationName}"`);
  });
  
  return result;
};

/**
 * Get all schedules formatted for database save
 */
export const getSchedulesForSave = (): CreativeSchedule[] => {
  return getAllSchedules();
};
